import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import DashboardLayout from "../../../components/dashboard/DashboardLayout";
import {
  ConfirmDialog,
  EmptyState,
  ErrorState,
  LoadingState,
} from "../../../components/dashboard/DashboardStates";
import { useToast } from "../../../context/ToastContext";
import * as pagesApi from "../../../api/pagesApi";
import * as sectionsApi from "../../../api/sectionsApi";
import { getApiErrorMessage } from "../../../utils/apiResponse";
import { SECTION_TYPES } from "../../../utils/sectionTypes";
import SectionFormModal from "./SectionFormModal";

const SectionsManager = () => {
  const { pageId } = useParams();
  const toast = useToast();
  const [page, setPage] = useState(null);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [pageData, sectionData] = await Promise.all([
        pagesApi.getPageById(pageId),
        sectionsApi.getSectionsByPage(pageId),
      ]);
      setPage(pageData);
      setSections(sectionData || []);
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [pageId]);

  const handleSave = async (payload) => {
    setBusy(true);
    try {
      if (editing) {
        await sectionsApi.updateSection(editing.id, {
          ...payload,
          rowVersion: editing.rowVersion,
        });
        toast.success("Section updated.");
      } else {
        await sectionsApi.createSection(pageId, payload);
        toast.success("Section created.");
      }
      setModalOpen(false);
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    setBusy(true);
    try {
      await sectionsApi.deleteSection(deleting.id);
      toast.success("Section deleted.");
      setDeleting(null);
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <DashboardLayout
      title={page ? `Sections · ${page.name}` : "Sections"}
      subtitle={page ? `Slug: ${page.slug}` : "Manage sections for this page."}
    >
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Link
          to="/dashboard/pages"
          className="text-sm font-medium text-emerald-700 hover:underline"
        >
          ← Back to pages
        </Link>
        <button
          type="button"
          onClick={() => {
            setEditing(null);
            setModalOpen(true);
          }}
          className="rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800"
        >
          Add section
        </button>
      </div>

      {loading ? <LoadingState label="Loading sections…" /> : null}
      {!loading && error ? <ErrorState message={error} /> : null}

      {!loading && !error && sections.length === 0 ? (
        <EmptyState
          title="No sections yet"
          description="Add a section to start attaching fields and media."
        />
      ) : null}

      {!loading && !error && sections.length > 0 ? (
        <div className="grid gap-4">
          {sections
            .slice()
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map((section) => (
              <div
                key={section.id}
                className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
              >
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-base font-semibold text-slate-900">
                        {section.name}
                      </h3>
                      <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
                        {section.sectionType}
                      </span>
                      <span
                        className={[
                          "rounded-full px-2.5 py-1 text-xs font-semibold",
                          section.isPublished
                            ? "bg-emerald-50 text-emerald-700"
                            : "bg-amber-50 text-amber-700",
                        ].join(" ")}
                      >
                        {section.isPublished ? "Published" : "Draft"}
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-slate-500">
                      Key: <code className="text-slate-700">{section.key}</code> · Sort{" "}
                      {section.sortOrder}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Link
                      to={`/dashboard/sections/${section.id}`}
                      className="rounded-xl bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white"
                    >
                      Edit content
                    </Link>
                    <button
                      type="button"
                      onClick={() => {
                        setEditing(section);
                        setModalOpen(true);
                      }}
                      className="rounded-xl border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700"
                    >
                      Settings
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeleting(section)}
                      className="rounded-xl border border-rose-200 px-3 py-1.5 text-xs font-semibold text-rose-700"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
        </div>
      ) : null}

      <SectionFormModal
        open={modalOpen}
        initial={editing}
        sectionTypes={SECTION_TYPES}
        busy={busy}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        onSubmit={handleSave}
      />

      <ConfirmDialog
        open={Boolean(deleting)}
        title="Delete section"
        description={
          deleting
            ? `Delete “${deleting.name}” and its fields/media records?`
            : ""
        }
        busy={busy}
        onCancel={() => setDeleting(null)}
        onConfirm={handleDelete}
      />
    </DashboardLayout>
  );
};

export default SectionsManager;
